/*
 * The Frosted Muzzle — mobile navigation drawer (Phase 3 / Global Chrome).
 *
 * Accessible off-canvas drawer: focus moves to the close button on open,
 * Tab/Shift+Tab cycle within the drawer (focus trap), ESC closes, and the
 * rest of the page is marked `inert` while open. Does not touch Dawn's
 * pub/sub, global, or details-disclosure modules.
 */
class FMMobileNav extends HTMLElement {
  connectedCallback() {
    this.sectionWrapper = this.closest('.shopify-section') || this;
    this.overlay = this.querySelector('[data-mobile-nav-overlay]');
    this.closeButton = this.querySelector('[data-mobile-nav-close]');
    this.toggles = document.querySelectorAll('[data-mobile-nav-toggle]');
    this.onKeydown = this.handleKeydown.bind(this);

    this.toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => this.open(toggle));
    });
    if (this.closeButton) this.closeButton.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());
  }

  open(trigger) {
    this.activeTrigger = trigger || this.toggles[0];
    this.setAttribute('open', '');
    document.body.classList.add('fm-mobile-nav-open');
    this.setPageInert(true);
    this.toggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
    document.addEventListener('keydown', this.onKeydown);
    requestAnimationFrame(() => {
      if (this.closeButton) this.closeButton.focus();
    });
  }

  close() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    document.body.classList.remove('fm-mobile-nav-open');
    this.setPageInert(false);
    this.toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    document.removeEventListener('keydown', this.onKeydown);
    if (this.activeTrigger && typeof this.activeTrigger.focus === 'function') {
      this.activeTrigger.focus();
    }
  }

  // Mark every top-level page region except this drawer's section as inert,
  // hiding it from AT and removing it from the tab order while the drawer is open.
  setPageInert(state) {
    Array.from(document.body.children).forEach((el) => {
      if (el === this.sectionWrapper) return;
      if (state) {
        if (el.hasAttribute('inert')) return;
        el.setAttribute('inert', '');
        el.dataset.fmInertAdded = 'true';
      } else if (el.dataset.fmInertAdded) {
        el.removeAttribute('inert');
        delete el.dataset.fmInertAdded;
      }
    });
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = this.getFocusableElements();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  getFocusableElements() {
    const selector =
      'a[href], button:not([disabled]), summary, input, [tabindex]:not([tabindex="-1"])';
    return Array.from(this.querySelectorAll(selector)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
    );
  }
}

if (!customElements.get('mobile-nav')) {
  customElements.define('mobile-nav', FMMobileNav);
}
